/**
 * LT ve UTC zaman yardımcıları (ADR 0057 §2). Saklama UTC an + saat farkıdır (CLAUDE.md "Zaman");
 * giriş ve gösterim yerel saat (LT) ya da UTC olabilir. Saat farkı dakika hassasiyetinde,
 * −12:00…+14:00 (sefer raporu şeması; ADR 0065 §8). Saf fonksiyonlar.
 */

const MINUTE = 60_000;

/** Listede hazır saat farkları (dakika): −12:00 … +14:00, 30 dakika adım; başkası yazılabilir. */
export const TZ_OFFSET_STEPS: readonly number[] = Array.from(
  { length: 53 },
  (_, i) => -720 + i * 30,
);

const MIN_OFFSET = -720;
const MAX_OFFSET = 840;

/** Şemanın kabul ettiği fark mı: tam dakika, −12:00…+14:00 (ADR 0065 §8). */
export function isOffset(minutes: number | null | undefined): minutes is number {
  return (
    typeof minutes === 'number' &&
    Number.isInteger(minutes) &&
    minutes >= MIN_OFFSET &&
    minutes <= MAX_OFFSET
  );
}

/**
 * Seçim listesi: hazır adımlar ve mevcut fark (adıma uymuyorsa sırasına eklenir; saat kaydından
 * gelen +02:40 ya da +05:45 listede görünür).
 */
export function offsetChoices(
  current: number | null | undefined,
  steps: readonly number[] = TZ_OFFSET_STEPS,
): readonly number[] {
  if (!isOffset(current) || steps.includes(current)) return steps;
  return [...steps, current].sort((a, b) => a - b);
}

/** Yazılan fark: `+05:45`, `UTC -03:30`, `2:40`, `+0545`; geçersizse `null`. */
export function parseOffset(text: string): number | null {
  const match = /^(?:UTC|GMT)?\s*([+\-−])?\s*(\d{1,2}):?(\d{2})$/i.exec(text.trim());
  if (!match) return null;
  const minutes = Number(match[3]);
  if (minutes > 59) return null;
  const total = Number(match[2]) * 60 + minutes;
  const value = match[1] === '-' || match[1] === '−' ? -total : total;
  return isOffset(value) ? value : null;
}

/** `UTC +03:00` biçimi. */
export function offsetLabel(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `UTC ${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/** UTC an → saat farkıyla `YYYY-MM-DDTHH:mm` (`DateTimeInput` değeri). */
export function zonedInput(iso: string, offsetMin: number): string {
  return new Date(Date.parse(iso) + offsetMin * MINUTE).toISOString().slice(0, 16);
}

/** `YYYY-MM-DDTHH:mm` (saat farkıyla) → UTC an; geçersizse `null`. */
export function fromZonedInput(local: string, offsetMin: number): string | null {
  const at = Date.parse(`${local}:00.000Z`);
  return Number.isNaN(at) ? null : new Date(at - offsetMin * MINUTE).toISOString();
}

/** `GG.AA.YYYY SS:DD`, saat farkıyla; boşsa `—`. */
export function zonedStamp(iso: string | null | undefined, offsetMin = 0): string {
  if (!iso || Number.isNaN(Date.parse(iso))) return '—';
  const local = zonedInput(iso, offsetMin);
  return `${local.slice(8, 10)}.${local.slice(5, 7)}.${local.slice(0, 4)} ${local.slice(11, 16)}`;
}

/** LT ve UTC birlikte: `15.10.2026 12:00 LT (UTC +03:00) · 15.10.2026 09:00 UTC`. */
export function ltUtcText(iso: string | null | undefined, offsetMin: number): string {
  if (!iso || Number.isNaN(Date.parse(iso))) return '—';
  return `${zonedStamp(iso, offsetMin)} LT (${offsetLabel(offsetMin)}) · ${zonedStamp(iso)} UTC`;
}

export interface ZoneOption {
  zone: string;
  offset: number;
}

/** Bölgenin o andaki farkı (dakika); bilinmeyen bölge `null`. */
export function zoneOffsetAt(zone: string, at: number): number | null {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(new Date(at))
      .find((p) => p.type === 'timeZoneName')?.value;
    if (part === 'GMT') return 0;
    const match = /GMT([+-])(\d{2}):(\d{2})/.exec(part ?? '');
    if (!match) return null;
    const minutes = Number(match[2]) * 60 + Number(match[3]);
    return match[1] === '-' ? -minutes : minutes;
  } catch {
    return null;
  }
}

/**
 * IANA bölge adları ve verilen andaki farkları (ADR 0057 §2): şemanın kabul ettiği farklar
 * (tam dakika, −12:00…+14:00; Nepal +05:45 ve Chatham +12:45 dahil, ADR 0065 §8).
 */
export function ianaZoneOptions(
  at: number,
  zones: readonly string[] = supportedZones(),
): ZoneOption[] {
  const options: ZoneOption[] = [];
  for (const zone of zones) {
    const offset = zoneOffsetAt(zone, at);
    if (!isOffset(offset)) continue;
    options.push({ zone, offset });
  }
  return options.sort((a, b) => a.zone.localeCompare(b.zone));
}

function supportedZones(): string[] {
  const intl = Intl as { supportedValuesOf?: (key: string) => string[] };
  return intl.supportedValuesOf?.('timeZone') ?? [];
}
